import * as React from "react";
import { useDispatch, useSelector } from "@teamprevent/admin-redux";
import { values, flatten, uniq } from "lodash";
import { Container } from "semantic-ui-react";

import { AccordionQuestionnaireContainer } from "@teamprevent/common/questionnaires/components/AccordionQuestionnaireContainer";
import { AccordionQuestionnaireSection } from "@teamprevent/common/questionnaires/components/AccordionQuestionnaireSection";
import { TextQuestion } from "@teamprevent/common/questionnaires/components/questions/TextQuestion";
import { clearQuestionnaire, submitInvalidQuestionnaire as submitInvalidQuestionnaireAction } from "@teamprevent/common/questionnaires/actions";
import { YesNoNaDropdownQuestion } from "@teamprevent/common/questionnaires/components/questions/YesNoNaDropdownQuestion";
import { navigateToParentRoute } from "@teamprevent/common/routing/actions";
import { TaskTypes } from "@teamprevent/common/task/taskActions/models";
import { QuestionnaireTypes } from "@teamprevent/common/questionnaires/model";
import { toast } from "@teamprevent/common/toasts";
import { getFeatureToggles } from "@teamprevent/common/getFeatureToggles";
import * as FeatureToggleNames from "@teamprevent/common/featureToggleNames";

import { ApplicationState } from "../../applicationState";
import { ConfidentialitySection } from "../../consultations/components/ConfidentialitySection";
import { DataProtectionSection } from "../../consultations/components/DataProtectionSection";
import { routeIdSelector as customerIdSelector } from "../../crud/customer/selectors";
import { ohCaseSelector, routeIdSelector as caseIdSelector } from "../../crud/ohCase/selectors";
import { employeeRecordSelector } from "../../crud/employeeRecord/selectors";
import { submitBbvReport, loadBbvReport } from "../actions";
import { AddTaskFormModel, ExcludeBbvTaskTypes, mapToCreateTaskModel } from "../../task/model";
import { AddTasksInputs } from "../../tasks/components/addTasksInputs";
import { loadBbvReportSuccessorTasks } from "../actions";
import { taskIdSelector } from "../../task/selectors";
import { TasksApi } from "../../tasks/tasksApi";
import { validateConfidentialitySection } from "../../consultations/components/confidentialitySectionValidation";

import { BbvReportDidNotAttendButton } from "./BbvReportDidNotAttendButton";

interface TaskFormModel {
    task: AddTaskFormModel;
    isValid: boolean;
}

export const BBV_REPORT_QUESTIONNAIRE_KEY = "BbvReport";

export const BbvReport: React.FunctionComponent = () => {

    const dispatch = useDispatch();

    const [submitted, setSubmitted] = React.useState<boolean>(false);
    const [tasks, setTasks] = React.useState<TaskFormModel[]>([]);

    const customerId = useSelector((state: ApplicationState) => customerIdSelector(state));
    const ohCaseId = useSelector((state: ApplicationState) => caseIdSelector(state));
    const ohCase = useSelector((state: ApplicationState) => ohCaseSelector(state));
    const employeeRecord = useSelector((state: ApplicationState) => employeeRecordSelector(state));
    const questionnaireState = useSelector((state: ApplicationState) => state.questionnaires[BBV_REPORT_QUESTIONNAIRE_KEY]);
    const taskId = useSelector((state: ApplicationState) => taskIdSelector(state));
    const bbvReportSuccessorTasks = useSelector((state: ApplicationState) => state.bbvReportSuccessorTasks);

    const successorTasks = bbvReportSuccessorTasks?.filter(t => !ExcludeBbvTaskTypes.includes(TaskTypes[t.typeId]));

    const task = (ohCase?.tasks && ohCase.tasks?.find(t => t.id === taskId));
    const readonly = task && task.completed;

    const questions = questionnaireState ? uniq(flatten(questionnaireState.sections.map(section => values(section.questions)))) : [];

    React.useEffect(() => {
        if (!successorTasks || !successorTasks.length) {
            loadSuccessorTasks();
        }

        return () => {
            dispatch(clearQuestionnaire(BBV_REPORT_QUESTIONNAIRE_KEY));
        };
    }, []);

    const loadQuestionnaire = () => {
        dispatch(loadBbvReport(ohCaseId, taskId, BBV_REPORT_QUESTIONNAIRE_KEY));
    };

    const submitQuestionnaire = async (): Promise<boolean> => {
        setSubmitted(true);

        if (questions.some(q => !q.isValid) || tasks.some(t => !t.isValid)) {
            submitInvalidQuestionnaire();
            return false;
        }

        const immunisationWorkflowImprovementsPt1 = getFeatureToggles()[FeatureToggleNames.ImmunisationWorkflowImprovementsPt1];

        const validationResult = validateConfidentialitySection(questions);

        if (immunisationWorkflowImprovementsPt1 && !validationResult.isValid) {
            toast.warning(validationResult.error);
            submitInvalidQuestionnaire();
            return false;
        }

        const createTasks = tasks.map(t => mapToCreateTaskModel(t.task, ohCaseId));

        await dispatch(submitBbvReport(ohCaseId, taskId, questions, BBV_REPORT_QUESTIONNAIRE_KEY, createTasks));

        navigateBack();

        return true;
    };

    const submitInvalidQuestionnaire = () => {
        dispatch(submitInvalidQuestionnaireAction(BBV_REPORT_QUESTIONNAIRE_KEY, true));
    };

    const navigateBack = () => {
        dispatch(navigateToParentRoute());
    };

    const loadSuccessorTasks = () => {
        dispatch(loadBbvReportSuccessorTasks());
    };

    const onSetTaskInputs = (updatedTasks: TaskFormModel[]) => setTasks(updatedTasks);

    const extraSections = !readonly && successorTasks ? [{
        id: "NextTask",
        title: "Next Task",
        content: (
            <AddTasksInputs
                taskTypes={successorTasks}
                onChange={onSetTaskInputs}
                ohCaseId={ohCaseId}
                showErrors={submitted}
                assignmentOptionRequired={true}
            />)
    }] : [];

    return (
        <Container>
            <h2>
                BBV Consultation for {ohCase && `${ohCase.employeeFirstName || ""} ${ohCase.employeeLastName || ""}`}
            </h2>
            <BbvReportDidNotAttendButton customerId={customerId} ohCaseId={ohCaseId} readonly={readonly} task={task} />
            <AccordionQuestionnaireContainer
                loadQuestionnaire={loadQuestionnaire}
                questionnaireKey={BBV_REPORT_QUESTIONNAIRE_KEY}
                readonly={readonly}
                submitQuestionnaire={submitQuestionnaire}
                questionnaireType={QuestionnaireTypes.ClinicalNotes}
                task={task}
                removeAppointment={() => new TasksApi().removeAppointment(task.caseId, task.id)}
            >
                <ConfidentialitySection
                    index={0}
                    ohCase={ohCase}
                    employeeRecord={employeeRecord}
                    questionnaireKey={BBV_REPORT_QUESTIONNAIRE_KEY}
                    readonly={readonly}
                />
                <DataProtectionSection
                    index={1}
                    questionnaireKey={BBV_REPORT_QUESTIONNAIRE_KEY}
                    readonly={readonly}
                />
                <AccordionQuestionnaireSection id="BbvConsultation" title="BBV Follow Up Appointment Notes">
                    <TextQuestion
                        inline
                        readonly={readonly}
                        multiline
                        // eslint-disable-next-line max-len
                        questionConfig={({ id: "CommentsAndNotes", questionText: "Comments / Notes" })}
                    />
                    <YesNoNaDropdownQuestion
                        readonly={readonly}
                        questionConfig={({ id: "RiddorReportable", questionText: "Has this incident become RIDDOR reportable? (ie is the source know to be HIV/HepB/HepC Positive. If yes please ensure that a “report RIDDOR task is produced." })} // eslint-disable-line max-len
                        mandatory
                    />
                    <YesNoNaDropdownQuestion
                        readonly={readonly}
                        questionConfig={({ id: "SourceBlood", questionText: "Have source blood been obtained? If yes please ensure that the employee been notified of the outcome and have any outstanding follow up bloods been cancelled?" })} // eslint-disable-line max-len
                        mandatory
                    />
                </AccordionQuestionnaireSection>
                {!readonly && extraSections && extraSections.map((s, i) =>
                    (<AccordionQuestionnaireSection key={i} title={s.title} id={s.id} hideNextButton={true} active={true} neverHide={true}>
                        {s.content}
                    </AccordionQuestionnaireSection>))
                }
            </AccordionQuestionnaireContainer>
        </Container>
    );
};
